/**
 * damage_state — PERSISTENT MATERIAL DAMAGE, the pure half (shader gaps program round 2, 2026-09-29; the owner's report
 * §4.5 / §14.7). No three.js here: the harness (`verify:damage`) pins every rule below in node.
 *
 * WHAT IT STORES. Not another dark quad over the surface (that is `impact_decals`) but MATERIAL STATE the receiver's own
 * material reads BEFORE lighting: per RECEIVER a capped ring of hit records `{ local point, local normal, radius, kind,
 * profile, energy, time, seed }` in the receiver's LOCAL frame, so
 *   • a hit on a moving hull rides the hull (the shader measures the fragment in the hull's frame, not the world's);
 *   • a hit on a static building survives its LOD swaps, crossfades and the instancer's swap-remove — the draw never
 *     needs to know which pool slot or which LOD level it is: static records are resolved to RENDER space for the
 *     frame (`packStatic`) and every representation drawn there shows them;
 *   • a re-cook (xatlas re-unwraps every bake) never invalidates anything — nothing is a UV tile.
 *
 * RECEIVER IDENTITY is a stable key, never a draw slot (pools swap-remove, crowds re-pack, a LOD crossfade draws two
 * representations): a settlement building by its settlement + placement (`settleReceiverId`), a player structure by its
 * structure id, a hull by its ride / air-target key, the terrain by a 64 m world cell (`groundCellId`), anything else
 * static by a 16 m cell (`staticCellId`).
 *
 * THE RESPONSE is authored per SURFACE CLASS × WEAPON CLASS (`profileFor`): coated metal STRIPS (the paint goes, bare
 * iron shows — metalness 1, a moving highlight — ringed by heat tint and burnt paint), rock CHIPS (a lighter fresh
 * fracture, rough, a pock), wood CHARS (2 % charcoal, rough, embers that cool), soil and sand GOUGE (a crater, darker
 * fresh earth), glass and crystal MELT (frosted, crazed). The class comes from what `surface_response` already knows —
 * the collider class and, for ground, the SEEDED BIOME — or from a settlement prefab key (`surfaceForPrefabKey`), never
 * from the display material's colour or UUID (both break on a re-cook).
 *
 * HEAT is emission that fades over HEAT_FADE_S (20 s); CHAR and the stripped metal stay for the session. The whole
 * store is client memory, capped by bytes (DAMAGE_BYTES_CAP) and cleared on a world change. No server row: nothing in
 * gameplay reads it (the report: persist only where gameplay requires it).
 */

// ─── classes ───────────────────────────────────────────────────────────────

export type DamageKind = 'strip' | 'char' | 'gouge' | 'chip' | 'melt';
export type SurfaceClass = 'coated_metal' | 'rock' | 'wood' | 'soil' | 'sand' | 'glass' | 'crystal';
export type WeaponClass = 'energy' | 'kinetic' | 'blast';
export type ReceiverType = 'static' | 'ground' | 'mover';

export const SURFACES: readonly SurfaceClass[] = ['coated_metal', 'rock', 'wood', 'soil', 'sand', 'glass', 'crystal'];
export const WEAPONS: readonly WeaponClass[] = ['energy', 'kinetic', 'blast'];
export const PROFILE_COUNT = SURFACES.length * WEAPONS.length;

/** The damage kind each surface answers with. */
export const SURFACE_KIND: Record<SurfaceClass, DamageKind> = {
  coated_metal: 'strip', rock: 'chip', wood: 'char', soil: 'gouge', sand: 'gouge', glass: 'melt', crystal: 'melt',
};
/** The probe lever's reverse map: a kind → the surface that shows it. */
export const KIND_SURFACE: Record<DamageKind, SurfaceClass> = { strip: 'coated_metal', chip: 'rock', char: 'wood', gouge: 'soil', melt: 'glass' };

// ─── tuning ────────────────────────────────────────────────────────────────

/** Heat (emission) fades to nothing over this many seconds; char and bare metal stay. */
export const HEAT_FADE_S = 20;
/** Per-receiver ring caps (records). The oldest leaves first. */
export const RING_CAP: Record<ReceiverType, number> = { static: 32, ground: 48, mover: 24 };
/** The whole store's memory cap (bytes, as accounted below). */
export const DAMAGE_BYTES_CAP = 256 * 1024;
/** Accounting sizes: a record is 12 numbers (+ its derived world pose), a receiver its frame + bookkeeping. */
export const RECORD_BYTES = 96;
export const RECEIVER_BYTES = 160;
/** A new hit this close to a same-profile record (fraction of the larger radius) MERGES into it (a burst on one spot). */
export const MERGE_FRAC = 0.3;
/** A merged burst's mark grows to at most this multiple of one hit's radius. */
export const MERGE_GROW = 1.8;
export const RADIUS_MIN_M = 0.05;
export const RADIUS_MAX_M = 4;
/** Base mark radius (m) per weapon, before the punch scale. */
export const WEAPON_RADIUS_M: Record<WeaponClass, number> = { energy: 0.22, kinetic: 0.11, blast: 1.1 };
export const WEAPON_ENERGY: Record<WeaponClass, number> = { energy: 0.85, kinetic: 0.5, blast: 1 };
/** Cell sizes (m): ground receivers, and the static fallback for colliders no owner registered. */
export const GROUND_CELL_M = 64;
export const STATIC_CELL_M = 16;
/** The mask's shape: a thin ELLIPSOID along the hit plane — the slab half-thickness is max(SLAB_MIN_M, SLAB_R·R). */
export const SLAB_MIN_M = 0.3;
export const SLAB_R = 0.45;
/** The fragment's GEOMETRY normal must face the hit's normal (a wall's far face, a hull's underside stay clean). */
export const FACING_E0 = 0.05;
export const FACING_E1 = 0.45;
/** The mark's outer edge (in normalized radius) where the whole mark fades out. */
export const EDGE_E0 = 0.82;
/** The heat-tint (temper) colours of oxidised steel, inner (hottest: blue) → outer (straw) — AWS / heat-tint tables. */
export const TEMPER_RAMP: ReadonlyArray<readonly [number, number, number]> = [
  [0.10, 0.14, 0.42], // dark blue   (~600 °C)
  [0.30, 0.12, 0.30], // purple      (~450 °C)
  [0.40, 0.24, 0.11], // brown       (~390 °C)
  [0.62, 0.50, 0.24], // straw       (~340 °C)
];

// ─── profiles ──────────────────────────────────────────────────────────────

/** One authored response. Albedo/roughness/metalness are AFFINE in the material's own value: out = base·K + C. */
export interface DamageProfile {
  surface: SurfaceClass;
  weapon: WeaponClass;
  kind: DamageKind;
  /** The CORE (where the surface itself changed). */
  albedoK: number; albedoC: [number, number, number];
  roughK: number; roughC: number;
  metalK: number; metalC: number;
  /** Core radius as a fraction of the mark radius (the rest is the temper band + the rim). */
  coreFrac: number;
  /** The RIM: the surface around the core, scorched / dusted — albedo × rimAlbedoK, roughness → rimRoughC. */
  rimAlbedoK: number; rimRoughC: number;
  /** The heat-tint band's width (fraction of R) between core and rim — metal only (0 elsewhere). */
  temperW: number;
  /** The crater: bowl slope scale (depth / radius), the raised lip, and where it ends (fraction of R). */
  crater: number; lip: number; craterR: number;
  /** Edge noise amount (0 = a clean circle, 1 = a ragged flaking edge). */
  edgeNoise: number;
  /** Heat per unit energy (emission at the core, fading over HEAT_FADE_S). CPU-only: folded into each record's heat. */
  heatK: number;
  /** Radius multiplier for this surface (a gouge in soil is wider than a chip in rock). */
  radiusK: number;
}

type Base = Omit<DamageProfile, 'surface' | 'weapon' | 'kind'>;
const iron: [number, number, number] = [0.53, 0.51, 0.49]; // physicallybased.info: iron, linear
const charcoal: [number, number, number] = [0.022, 0.02, 0.018]; // charcoal ≈ 2 % (physicallybased.info)

/** The energy-weapon response per surface (the kinetic and blast variants derive from it in `profileFor`). */
const BASE: Record<SurfaceClass, Base> = {
  coated_metal: { albedoK: 0, albedoC: iron, roughK: 0, roughC: 0.32, metalK: 0, metalC: 1, coreFrac: 0.42, rimAlbedoK: 0.22, rimRoughC: 0.86, temperW: 0.2, crater: 0.06, lip: 0.02, craterR: 0.55, edgeNoise: 0.65, heatK: 1, radiusK: 1 },
  rock: { albedoK: 1.22, albedoC: [0.05, 0.05, 0.05], roughK: 0, roughC: 0.93, metalK: 0, metalC: 0, coreFrac: 0.55, rimAlbedoK: 0.55, rimRoughC: 0.9, temperW: 0, crater: 0.25, lip: 0.05, craterR: 0.62, edgeNoise: 0.85, heatK: 0.35, radiusK: 1 },
  wood: { albedoK: 0, albedoC: charcoal, roughK: 0, roughC: 0.95, metalK: 0, metalC: 0, coreFrac: 0.5, rimAlbedoK: 0.35, rimRoughC: 0.9, temperW: 0, crater: 0.08, lip: 0, craterR: 0.5, edgeNoise: 0.7, heatK: 0.8, radiusK: 1.1 },
  soil: { albedoK: 0.55, albedoC: [0, 0, 0], roughK: 0, roughC: 1, metalK: 0, metalC: 0, coreFrac: 0.6, rimAlbedoK: 0.8, rimRoughC: 0.97, temperW: 0, crater: 0.45, lip: 0.15, craterR: 0.72, edgeNoise: 0.5, heatK: 0.25, radiusK: 1.25 },
  sand: { albedoK: 0.78, albedoC: [0, 0, 0], roughK: 0, roughC: 1, metalK: 0, metalC: 0, coreFrac: 0.65, rimAlbedoK: 0.92, rimRoughC: 1, temperW: 0, crater: 0.35, lip: 0.18, craterR: 0.78, edgeNoise: 0.45, heatK: 0.4, radiusK: 1.3 },
  glass: { albedoK: 0.5, albedoC: [0.35, 0.36, 0.38], roughK: 0, roughC: 0.55, metalK: 0, metalC: 0, coreFrac: 0.45, rimAlbedoK: 0.9, rimRoughC: 0.35, temperW: 0, crater: 0.1, lip: 0.05, craterR: 0.5, edgeNoise: 0.4, heatK: 1, radiusK: 0.9 },
  crystal: { albedoK: 0.7, albedoC: [0.25, 0.3, 0.34], roughK: 0, roughC: 0.5, metalK: 0, metalC: 0, coreFrac: 0.45, rimAlbedoK: 0.9, rimRoughC: 0.3, temperW: 0, crater: 0.12, lip: 0.05, craterR: 0.5, edgeNoise: 0.5, heatK: 1, radiusK: 0.9 },
};

/** The authored response for a surface hit by a weapon class. Pure; the GPU table is `packProfiles`. */
export function profileFor(surface: SurfaceClass, weapon: WeaponClass): DamageProfile {
  const b = BASE[surface] ?? BASE.coated_metal;
  const p: DamageProfile = { ...b, albedoC: [...b.albedoC] as [number, number, number], surface, weapon, kind: SURFACE_KIND[surface] };
  if (weapon === 'kinetic') {
    // a slug: no heat, a deeper punched dent, no temper band; wood SPLINTERS (fresh pale wood) instead of charring
    p.heatK = 0; p.temperW = 0; p.crater = Math.max(p.crater, 0.3); p.lip = Math.max(p.lip, 0.06);
    p.rimAlbedoK = Math.max(p.rimAlbedoK, 0.85);
    if (surface === 'coated_metal') { p.coreFrac = 0.55; p.roughC = 0.4; }
    if (surface === 'wood') { p.albedoK = 1.35; p.albedoC = [0.03, 0.025, 0.015]; p.roughC = 0.9; }
  } else if (weapon === 'blast') {
    p.crater = Math.max(p.crater, 0.12) * 1.4; p.lip *= 1.4; p.rimAlbedoK *= 0.7; p.heatK *= 1.2;
    if (surface === 'coated_metal') { p.coreFrac = 0.35; p.temperW = 0.25; }
  }
  return p;
}
export function profileIndex(surface: SurfaceClass, weapon: WeaponClass): number {
  const s = Math.max(0, SURFACES.indexOf(surface)), w = Math.max(0, WEAPONS.indexOf(weapon));
  return s * WEAPONS.length + w;
}
export function profileAt(index: number): DamageProfile {
  const i = Math.max(0, Math.min(PROFILE_COUNT - 1, Math.round(index)));
  return profileFor(SURFACES[Math.floor(i / WEAPONS.length)], WEAPONS[i % WEAPONS.length]);
}
/** PROFILE_COUNT × 4 vec4 (P0 albedoC + K · P1 rough K,C metal K,C · P2 coreFrac, rimAlbedoK, rimRoughC, temperW · P3 crater, lip, edgeNoise, craterR). */
export function packProfiles(): Float32Array {
  const out = new Float32Array(PROFILE_COUNT * 16);
  for (let i = 0; i < PROFILE_COUNT; i++) {
    const p = profileAt(i), o = i * 16;
    out.set([p.albedoC[0], p.albedoC[1], p.albedoC[2], p.albedoK, p.roughK, p.roughC, p.metalK, p.metalC,
      p.coreFrac, p.rimAlbedoK, p.rimRoughC, p.temperW, p.crater, p.lip, p.edgeNoise, p.craterR], o);
  }
  return out;
}

// ─── surface resolution (what surface_response already knows) ──────────────

/** Ground biome → surface (the SEED decides this, so it survives a re-cook — surface_response's BIOME_MATERIAL). */
export const BIOME_SURFACE: Record<string, SurfaceClass> = {
  dune_desert: 'sand', rocky_highlands: 'rock', crystal_forest: 'crystal', regolith_plains: 'soil', moss_wetland: 'soil', fungal_basin: 'soil',
};
/** PhysicsModule's collider class ('terrain' | 'structure' | 'other') or 'ground' (the analytic fallback) + the biome. */
export function surfaceForHit(collider: string | null, biome?: string | null): SurfaceClass {
  if (collider === 'terrain' || collider === 'ground') return BIOME_SURFACE[biome ?? ''] ?? 'soil';
  return 'coated_metal'; // buildings ring like metal (surface_response); an unclassified static reads the same
}
/** A settlement prefab key → its surface (the key is the catalog's, stable across re-cooks). */
export function surfaceForPrefabKey(key: string): SurfaceClass {
  const k = key.toLowerCase();
  if (/wood|timber|log|cabin|barn|hut|shack|plank|crate|palisade|stockade/.test(k)) return 'wood';
  if (/stone|rock|castle|temple|ruin|monolith|obelisk|brick|adobe|masonry|pyramid|cairn|tomb|crypt|mausoleum/.test(k)) return 'rock';
  if (/glass|crystal_spire|greenhouse/.test(k)) return 'glass';
  return 'coated_metal';
}
/** The gun's projectile kind → weapon class (impact_decals: ballistic → hole, energy → scorch). */
export function weaponForProjectile(kind: string | null | undefined): WeaponClass {
  return kind === 'slug' || kind === 'pellets' || kind === 'shell' ? 'kinetic' : 'energy';
}

// ─── stable ids ────────────────────────────────────────────────────────────

/** A settlement building: its site + its placement (the plan is deterministic; 10 cm quantization). */
export function settleReceiverId(siteId: number, wx: number, wz: number): string { return `settle:${siteId}:${Math.round(wx * 10)}:${Math.round(wz * 10)}`; }
export function deedReceiverId(structureId: string | number | bigint): string { return `structure:${String(structureId)}`; }
export function groundCellId(x: number, z: number): { id: string; cx: number; cz: number } {
  const cx = Math.floor(x / GROUND_CELL_M), cz = Math.floor(z / GROUND_CELL_M);
  return { id: `ground:${cx}:${cz}`, cx, cz };
}
export function staticCellId(x: number, y: number, z: number): { id: string; cx: number; cy: number; cz: number } {
  const cx = Math.floor(x / STATIC_CELL_M), cy = Math.floor(y / STATIC_CELL_M), cz = Math.floor(z / STATIC_CELL_M);
  return { id: `static:${cx}:${cy}:${cz}`, cx, cy, cz };
}
/** A structure solid with no registered owner (guild halls, barriers): keyed by its placement. */
export function solidReceiverId(s: { x: number; z: number; base: number }): string { return `solid:${Math.round(s.x * 10)}:${Math.round(s.z * 10)}:${Math.round(s.base * 10)}`; }

/** FNV-1a 32 — deterministic seeds from ids (no Math.random anywhere in the store). */
export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

// ─── frames (absolute metres, doubles) ─────────────────────────────────────

/** A receiver's frame: ABSOLUTE position, a unit quaternion, a per-axis scale (world = t + R·(S·local)). */
export interface Frame { px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number; sx: number; sy: number; sz: number }
export type Vec3 = [number, number, number];

export function identityFrame(px = 0, py = 0, pz = 0): Frame { return { px, py, pz, qx: 0, qy: 0, qz: 0, qw: 1, sx: 1, sy: 1, sz: 1 }; }
/** three's `rotation.y = yaw` (every placement in Atlas composes its instance with it) + a uniform scale. */
export function frameFromYaw(px: number, py: number, pz: number, yaw: number, scale = 1): Frame {
  return { px, py, pz, qx: 0, qy: Math.sin(yaw / 2), qz: 0, qw: Math.cos(yaw / 2), sx: scale, sy: scale, sz: scale };
}
/** A column-major 4×4 (three's `Matrix4.elements`) → frame; `ox/oy/oz` is added to the translation (render → absolute). */
export function frameFromMatrix(e: ArrayLike<number>, ox = 0, oy = 0, oz = 0): Frame {
  let sx = Math.hypot(e[0], e[1], e[2]); const sy = Math.hypot(e[4], e[5], e[6]), sz = Math.hypot(e[8], e[9], e[10]);
  const det = e[0] * (e[5] * e[10] - e[6] * e[9]) - e[4] * (e[1] * e[10] - e[2] * e[9]) + e[8] * (e[1] * e[6] - e[2] * e[5]);
  if (det < 0) sx = -sx;
  const m00 = e[0] / sx, m10 = e[1] / sx, m20 = e[2] / sx;
  const m01 = e[4] / sy, m11 = e[5] / sy, m21 = e[6] / sy;
  const m02 = e[8] / sz, m12 = e[9] / sz, m22 = e[10] / sz;
  // Shepperd (three's Quaternion.setFromRotationMatrix)
  let qx: number, qy: number, qz: number, qw: number;
  const tr = m00 + m11 + m22;
  if (tr > 0) { const s = 0.5 / Math.sqrt(tr + 1); qw = 0.25 / s; qx = (m21 - m12) * s; qy = (m02 - m20) * s; qz = (m10 - m01) * s; }
  else if (m00 > m11 && m00 > m22) { const s = 2 * Math.sqrt(1 + m00 - m11 - m22); qw = (m21 - m12) / s; qx = 0.25 * s; qy = (m01 + m10) / s; qz = (m02 + m20) / s; }
  else if (m11 > m22) { const s = 2 * Math.sqrt(1 + m11 - m00 - m22); qw = (m02 - m20) / s; qx = (m01 + m10) / s; qy = 0.25 * s; qz = (m12 + m21) / s; }
  else { const s = 2 * Math.sqrt(1 + m22 - m00 - m11); qw = (m10 - m01) / s; qx = (m02 + m20) / s; qy = (m12 + m21) / s; qz = 0.25 * s; }
  return { px: e[12] + ox, py: e[13] + oy, pz: e[14] + oz, qx, qy, qz, qw, sx, sy, sz };
}
function rotate(q: Frame, x: number, y: number, z: number, inverse: boolean): Vec3 {
  const qx = inverse ? -q.qx : q.qx, qy = inverse ? -q.qy : q.qy, qz = inverse ? -q.qz : q.qz, qw = q.qw;
  // v' = v + 2w(q×v) + 2q×(q×v)
  const tx = 2 * (qy * z - qz * y), ty = 2 * (qz * x - qx * z), tz = 2 * (qx * y - qy * x);
  return [x + qw * tx + (qy * tz - qz * ty), y + qw * ty + (qz * tx - qx * tz), z + qw * tz + (qx * ty - qy * tx)];
}
export function worldToLocal(f: Frame, x: number, y: number, z: number): Vec3 {
  const r = rotate(f, x - f.px, y - f.py, z - f.pz, true);
  return [r[0] / f.sx, r[1] / f.sy, r[2] / f.sz];
}
export function localToWorld(f: Frame, x: number, y: number, z: number): Vec3 {
  const r = rotate(f, x * f.sx, y * f.sy, z * f.sz, false);
  return [r[0] + f.px, r[1] + f.py, r[2] + f.pz];
}
function norm3(v: Vec3): Vec3 { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
/** A NORMAL world → local: the inverse-transpose of (R·S) is R·S⁻¹, so n_local ∝ S·R⁻¹·n. */
export function normalWorldToLocal(f: Frame, x: number, y: number, z: number): Vec3 {
  const r = rotate(f, x, y, z, true);
  return norm3([r[0] * f.sx, r[1] * f.sy, r[2] * f.sz]);
}
export function normalLocalToWorld(f: Frame, x: number, y: number, z: number): Vec3 {
  return norm3(rotate(f, x / f.sx, y / f.sy, z / f.sz, false));
}

// ─── records + receivers ───────────────────────────────────────────────────

export interface DamageRecord {
  /** Receiver-LOCAL point and unit normal (the canonical data). */
  lx: number; ly: number; lz: number; nx: number; ny: number; nz: number;
  radius: number;
  kind: DamageKind;
  profile: number;
  energy: number;
  /** Session time of the hit (s) and the heat at that moment (energy × the profile's heatK). */
  t: number;
  heat0: number;
  /** 0..1, deterministic (the id + the receiver's hit count) — the edge noise's offset. */
  seed: number;
  /** Derived: the ABSOLUTE world centre / normal under the receiver's current frame (static selection reads these). */
  wx: number; wy: number; wz: number;
}
export interface DamageReceiver {
  id: string;
  type: ReceiverType;
  surface: SurfaceClass;
  frame: Frame;
  records: DamageRecord[];
  hits: number;
  lastHitT: number;
  /** A registered owner (a deed, a settlement building) that went away is not DRAWN (its records wait, keyed by id). */
  live: boolean;
}
export interface HitInput {
  id: string;
  type: ReceiverType;
  surface: SurfaceClass;
  weapon: WeaponClass;
  frame: Frame;
  /** ABSOLUTE world hit point and outward surface normal. */
  x: number; y: number; z: number; nx: number; ny: number; nz: number;
  /** Radius scale (the weapon's punch; an oblique hit's stretch); energy scale (0..1.5). */
  punch?: number;
  energy?: number;
  /** Session time (s). */
  t: number;
}
export interface HitResult { receiver: DamageReceiver; record: DamageRecord; merged: boolean; local: Vec3 }

export function sameFrame(a: Frame, b: Frame): boolean {
  return a.px === b.px && a.py === b.py && a.pz === b.pz && a.qx === b.qx && a.qy === b.qy && a.qz === b.qz && a.qw === b.qw && a.sx === b.sx && a.sy === b.sy && a.sz === b.sz;
}
function smooth(e0: number, e1: number, x: number): number { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); }
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** Heat at session time `now` (0 after HEAT_FADE_S). */
export function heatAt(r: Pick<DamageRecord, 't' | 'heat0'>, now: number): number {
  const age = Math.max(0, now - r.t);
  if (age >= HEAT_FADE_S) return 0;
  const k = 1 - age / HEAT_FADE_S;
  return r.heat0 * k * k;
}

export class DamageState {
  readonly receivers = new Map<string, DamageReceiver>();
  readonly stats = { hits: 0, merged: 0, evictedRecords: 0, evictedReceivers: 0, cleared: 0, ringDrops: 0 };
  private recordCount = 0;
  private dirty_ = 1;
  last: { receiver: string; local: Vec3; world: Vec3; kind: DamageKind; surface: SurfaceClass; weapon: WeaponClass; radius: number; merged: boolean } | null = null;

  constructor(readonly bytesCap = DAMAGE_BYTES_CAP) {}

  /** Bumped on every change — the uploader re-selects when it moves. */
  get version(): number { return this.dirty_; }
  get records(): number { return this.recordCount; }
  get bytes(): number { return this.receivers.size * RECEIVER_BYTES + this.recordCount * RECORD_BYTES; }

  clear(): void {
    this.receivers.clear(); this.recordCount = 0; this.last = null; this.stats.cleared++; this.dirty_++;
  }

  receiver(id: string): DamageReceiver | null { return this.receivers.get(id) ?? null; }

  /** Make sure a receiver exists (an owner registering it); `live` true. The frame is updated if given. */
  ensure(id: string, type: ReceiverType, surface: SurfaceClass, frame: Frame): DamageReceiver {
    let r = this.receivers.get(id);
    if (!r) { r = { id, type, surface, frame, records: [], hits: 0, lastHitT: -1e9, live: true }; this.receivers.set(id, r); this.dirty_++; }
    else { if (!r.live) { r.live = true; this.dirty_++; } }
    return r;
  }
  /** An owner moved its receiver: the records RIDE (their local coordinates stay; the derived world pose follows). */
  setFrame(id: string, frame: Frame): void {
    const r = this.receivers.get(id);
    if (!r) return;
    r.frame = frame;
    for (const rec of r.records) this.derive(r, rec);
    this.dirty_++;
  }
  /** An owner went away: stop drawing its records (keep them, keyed by id); `drop` forgets them too (a demolished deed). */
  setLive(id: string, live: boolean, drop = false): void {
    const r = this.receivers.get(id);
    if (!r) return;
    if (drop) { this.recordCount -= r.records.length; this.receivers.delete(id); this.dirty_++; return; }
    if (r.live !== live) { r.live = live; this.dirty_++; }
  }

  private derive(r: DamageReceiver, rec: DamageRecord): void {
    const w = localToWorld(r.frame, rec.lx, rec.ly, rec.lz);
    rec.wx = w[0]; rec.wy = w[1]; rec.wz = w[2];
  }

  /** Record one hit. Returns the receiver + the record it wrote or merged into. */
  addHit(h: HitInput): HitResult {
    const r = this.ensure(h.id, h.type, h.surface, h.frame);
    if (!sameFrame(r.frame, h.frame)) { r.frame = h.frame; for (const rec of r.records) this.derive(r, rec); } // the owner moved it: the old marks ride
    const prof = profileFor(h.surface, h.weapon);
    const pi = profileIndex(h.surface, h.weapon);
    const local = worldToLocal(h.frame, h.x, h.y, h.z);
    const ln = normalWorldToLocal(h.frame, h.nx, h.ny, h.nz);
    // the radius is measured in WORLD metres; the record keeps it in the frame's units (÷ the mean scale)
    const sMean = (Math.abs(h.frame.sx) + Math.abs(h.frame.sy) + Math.abs(h.frame.sz)) / 3 || 1;
    const radiusW = Math.min(RADIUS_MAX_M, Math.max(RADIUS_MIN_M, WEAPON_RADIUS_M[h.weapon] * prof.radiusK * (h.punch ?? 1)));
    const radius = radiusW / sMean;
    const energy = Math.min(1.5, Math.max(0, (h.energy ?? 1) * WEAPON_ENERGY[h.weapon]));
    const heat0 = energy * prof.heatK;
    r.hits++; r.lastHitT = h.t; this.stats.hits++;
    // MERGE a burst on one spot into its record (a machine gun must not flood the ring with copies of one mark)
    for (let i = r.records.length - 1; i >= 0; i--) {
      const o = r.records[i];
      if (o.profile !== pi) continue;
      const d = Math.hypot(o.lx - local[0], o.ly - local[1], o.lz - local[2]);
      if (d > MERGE_FRAC * Math.max(o.radius, radius)) continue;
      if (o.nx * ln[0] + o.ny * ln[1] + o.nz * ln[2] < 0.8) continue;
      // a burst widens its mark a little (6 % a hit) up to MERGE_GROW × one hit's size — a scar, never a spreading stain
      o.radius = Math.min(RADIUS_MAX_M / sMean, Math.max(o.radius, Math.min(Math.max(o.radius, radius) * 1.06, radius * MERGE_GROW)));
      o.energy = Math.min(1.5, Math.max(o.energy, energy) + 0.1);
      o.heat0 = Math.max(heatAt(o, h.t), heat0);
      o.t = h.t;
      // keep it the NEWEST (the ring drops the oldest first; the composite draws newest on top)
      r.records.splice(i, 1); r.records.push(o);
      this.stats.merged++; this.dirty_++;
      this.last = { receiver: r.id, local, world: [h.x, h.y, h.z], kind: prof.kind, surface: h.surface, weapon: h.weapon, radius: o.radius * sMean, merged: true };
      return { receiver: r, record: o, merged: true, local };
    }
    const seed = (hash32(`${r.id}#${r.hits}`) % 100000) / 100000;
    const rec: DamageRecord = { lx: local[0], ly: local[1], lz: local[2], nx: ln[0], ny: ln[1], nz: ln[2], radius, kind: prof.kind, profile: pi, energy, t: h.t, heat0, seed, wx: 0, wy: 0, wz: 0 };
    this.derive(r, rec);
    r.records.push(rec); this.recordCount++;
    const cap = RING_CAP[r.type];
    while (r.records.length > cap) { r.records.shift(); this.recordCount--; this.stats.ringDrops++; }
    this.enforceBytes(r);
    this.dirty_++;
    this.last = { receiver: r.id, local, world: [h.x, h.y, h.z], kind: prof.kind, surface: h.surface, weapon: h.weapon, radius: radiusW, merged: false };
    return { receiver: r, record: rec, merged: false, local };
  }

  /** Over the byte cap: the least-recently-hit OTHER receiver gives up its oldest record (and goes when empty). */
  private enforceBytes(keep: DamageReceiver): void {
    while (this.bytes > this.bytesCap) {
      let victim: DamageReceiver | null = null;
      for (const r of this.receivers.values()) if (r !== keep && (!victim || r.lastHitT < victim.lastHitT)) victim = r;
      if (!victim) { // only the receiver being hit is left: it sheds its own oldest
        if (keep.records.length <= 1) break;
        keep.records.shift(); this.recordCount--; this.stats.evictedRecords++; continue;
      }
      if (victim.records.length > 0) { victim.records.shift(); this.recordCount--; this.stats.evictedRecords++; }
      if (victim.records.length === 0) { this.receivers.delete(victim.id); this.stats.evictedReceivers++; }
    }
  }

  /** Any record still hot at `now` (the uploader keeps re-writing heat while true). */
  anyHot(now: number): boolean {
    for (const r of this.receivers.values()) for (const rec of r.records) if (rec.heat0 > 0 && now - rec.t < HEAT_FADE_S) return true;
    return false;
  }

  /** The static + ground records nearest an ABSOLUTE camera point, within `rangeM`, nearest first, at most `max`. */
  selectStatic(cx: number, cy: number, cz: number, max: number, rangeM: number): Array<{ receiver: DamageReceiver; record: DamageRecord; d: number }> {
    const out: Array<{ receiver: DamageReceiver; record: DamageRecord; d: number }> = [];
    const r2 = rangeM * rangeM;
    for (const r of this.receivers.values()) {
      if (r.type === 'mover' || !r.live) continue;
      for (const rec of r.records) {
        const dx = rec.wx - cx, dy = rec.wy - cy, dz = rec.wz - cz;
        const d2 = dx * dx + dy * dy + dz * dz;
        // a mark's own radius counts: a 4 m crater whose centre is just past the range still shows at its edge
        const reach = rangeM + rec.radius * Math.max(Math.abs(r.frame.sx), Math.abs(r.frame.sy), Math.abs(r.frame.sz));
        if (d2 > r2 && d2 > reach * reach) continue;
        out.push({ receiver: r, record: rec, d: Math.sqrt(d2) });
      }
    }
    // nearest first; ties by time (newer first) — deterministic
    out.sort((a, b) => (a.d - b.d) || (b.record.t - a.record.t));
    if (out.length > max) out.length = max;
    // the COMPOSITE draws in record order (older under newer): re-sort the kept set by time
    out.sort((a, b) => (a.record.t - b.record.t) || (a.d - b.d));
    return out;
  }
}

// ─── the GPU record layout (shared with damage_layer) ──────────────────────

/** Three vec4 per record: A = (centre xyz, radius), B = (normal xyz, seed), C = (profile, heat, energy, 0). */
export const RECORD_VEC4 = 3;

/** Write one record into flat A/B/C arrays at slot `i`: `c` / `n` are in the SHADER's frame (render space or the mover's local). */
export function packRecordAt(A: Float32Array, B: Float32Array, C: Float32Array, i: number, c: Vec3, n: Vec3, radius: number, seed: number, profile: number, heat: number, energy: number): void {
  const o = i * 4;
  A[o] = c[0]; A[o + 1] = c[1]; A[o + 2] = c[2]; A[o + 3] = radius;
  B[o] = n[0]; B[o + 1] = n[1]; B[o + 2] = n[2]; B[o + 3] = seed * 97;
  C[o] = profile; C[o + 1] = heat; C[o + 2] = energy; C[o + 3] = 0;
}

/**
 * The static set in RENDER space: each record's absolute centre minus the render origin (doubles, then f32), its
 * WORLD normal and radius (static frames are rigid; the normal goes through the frame's rotation). Returns the count.
 */
export function packStatic(sel: ReadonlyArray<{ receiver: DamageReceiver; record: DamageRecord }>, A: Float32Array, B: Float32Array, C: Float32Array, start: number, max: number, origin: Vec3, now: number): number {
  let n = 0;
  for (const { receiver: r, record: rec } of sel) {
    if (n >= max) break;
    const nw = normalLocalToWorld(r.frame, rec.nx, rec.ny, rec.nz);
    const sMean = (Math.abs(r.frame.sx) + Math.abs(r.frame.sy) + Math.abs(r.frame.sz)) / 3;
    packRecordAt(A, B, C, start + n, [rec.wx - origin[0], rec.wy - origin[1], rec.wz - origin[2]], nw, rec.radius * sMean, rec.seed, rec.profile, heatAt(rec, now), rec.energy);
    n++;
  }
  return n;
}
/** A mover's records in its own LOCAL frame (the shader measures the fragment through the root's inverse matrix). */
export function packMover(r: DamageReceiver, A: Float32Array, B: Float32Array, C: Float32Array, start: number, max: number, now: number): number {
  let n = 0;
  for (const rec of r.records) {
    if (n >= max) break;
    packRecordAt(A, B, C, start + n, [rec.lx, rec.ly, rec.lz], [rec.nx, rec.ny, rec.nz], rec.radius, rec.seed, rec.profile, heatAt(rec, now), rec.energy);
    n++;
  }
  return n;
}

// ─── CPU mirrors of the shader (the harness pins these; damage_layer's TSL follows them line by line) ─────

export interface MaskOut { inside: number; core: number; band: number; rim: number; temperT: number; rho: number; dn: number }
/**
 * The mark's zones at a point `d` (= point − centre, in the record's frame) for a record of radius `R` and normal `n`,
 * seen by a fragment whose GEOMETRY normal is `g` (same frame). `noise01` is the edge noise sample (0.5 = none).
 */
export function damageMaskCpu(d: Vec3, n: Vec3, R: number, p: Pick<DamageProfile, 'coreFrac' | 'temperW' | 'edgeNoise'>, g: Vec3, noise01 = 0.5): MaskOut {
  const dn = d[0] * n[0] + d[1] * n[1] + d[2] * n[2];
  const tx = d[0] - n[0] * dn, ty = d[1] - n[1] * dn, tz = d[2] - n[2] * dn;
  const rho = Math.hypot(tx, ty, tz) / Math.max(1e-6, R);
  const slab = Math.max(SLAB_MIN_M, SLAB_R * R);
  const slabW = 1 - smooth(slab * 0.6, slab, Math.abs(dn));
  const facing = smooth(FACING_E0, FACING_E1, g[0] * n[0] + g[1] * n[1] + g[2] * n[2]);
  const rn = rho * (1 + p.edgeNoise * (noise01 - 0.5) * 0.5);
  const inside = (1 - smooth(EDGE_E0, 1, rn)) * slabW * facing;
  const core = 1 - smooth(p.coreFrac - 0.05, p.coreFrac + 0.03, rn);
  const band = p.temperW > 0 ? smooth(p.coreFrac - 0.03, p.coreFrac + 0.02, rn) * (1 - smooth(p.coreFrac + p.temperW - 0.03, p.coreFrac + p.temperW + 0.02, rn)) : 0;
  const temperT = p.temperW > 0 ? clamp01((rn - p.coreFrac) / p.temperW) : 0;
  const rimT = clamp01((rn - p.coreFrac - p.temperW) / Math.max(1e-4, 1 - p.coreFrac - p.temperW));
  const rim = Math.pow(1 - rimT, 1.5) * (1 - core) * (1 - band);
  return { inside, core: core * inside, band: band * inside, rim: rim * inside, temperT, rho: rn, dn };
}
/** The crater's radial SLOPE H'(ρc) (scale-free: heights scale with the mark), ρc = ρ / craterR. The normal tilts by −H'·r̂. */
export function craterSlopeCpu(rho: number, p: Pick<DamageProfile, 'crater' | 'lip' | 'craterR'>): number {
  const rc = rho / Math.max(1e-4, p.craterR);
  const bowl = rc < 1 ? p.crater * 4 * rc * (1 - rc * rc) : 0;
  const w = 0.18, u = (rc - 1) / w;
  const lip = p.lip * (-2 * u / w) * Math.exp(-u * u);
  return bowl + lip;
}
/** The heat-tint colour at t ∈ [0,1] across the band (inner blue → outer straw). */
export function temperColorCpu(t: number): Vec3 {
  const x = clamp01(t) * (TEMPER_RAMP.length - 1);
  const i = Math.min(TEMPER_RAMP.length - 2, Math.floor(x)), f = x - i;
  const a = TEMPER_RAMP[i], b = TEMPER_RAMP[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}
/**
 * The composite a set of records produces on one fragment (CPU oracle of the shader's affine accumulation): the
 * material's value → base·K + C per channel, over in record order (older under newer), zones rim → band → core.
 */
export function compositeCpu(base: { albedo: Vec3; rough: number; metal: number }, marks: ReadonlyArray<{ m: MaskOut; p: DamageProfile }>): { albedo: Vec3; rough: number; metal: number } {
  const aK: Vec3 = [1, 1, 1], aC: Vec3 = [0, 0, 0];
  let rK = 1, rC = 0, mK = 1, mC = 0;
  const over = (w: number, ak: Vec3, ac: Vec3, rk: number, rc: number, mk: number, mc: number, wm: number): void => {
    for (let c = 0; c < 3; c++) { aK[c] = aK[c] * (1 - w) + ak[c] * w; aC[c] = aC[c] * (1 - w) + ac[c] * w; }
    rK = rK * (1 - w) + rk * w; rC = rC * (1 - w) + rc * w;
    mK = mK * (1 - wm) + mk * wm; mC = mC * (1 - wm) + mc * wm;
  };
  for (const { m, p } of marks) {
    over(m.rim, [p.rimAlbedoK, p.rimAlbedoK, p.rimAlbedoK], [0, 0, 0], 0, p.rimRoughC, 1, 0, 0);
    if (m.band > 0) over(m.band, [0, 0, 0], temperColorCpu(m.temperT), 0, 0.36, 0, 1, m.band);
    over(m.core, [p.albedoK, p.albedoK, p.albedoK], p.albedoC, p.roughK, p.roughC, p.metalK, p.metalC, m.core);
  }
  return {
    albedo: [base.albedo[0] * aK[0] + aC[0], base.albedo[1] * aK[1] + aC[1], base.albedo[2] * aK[2] + aC[2]],
    rough: base.rough * rK + rC, metal: base.metal * mK + mC,
  };
}
